import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { auth, db } from '../firebase.js';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  updatePassword,
} from "firebase/auth";
import { ref, set, get } from "firebase/database";

const saveLocal = (user) => {
  try { localStorage.setItem("auth:v1", JSON.stringify(user)); } catch (e) {}
};

const removeLocal = () => {
  try { localStorage.removeItem("auth:v1"); } catch (e) {}
};

// Manual signup — blocks duplicate emails
export const signup = createAsyncThunk(
  "auth/signup",
  async ({ email, password, name, phone, isGoogleUser }, { rejectWithValue }) => {
    try {
      if (!auth) return rejectWithValue("Firebase Auth not configured");

      let uid;

      if (isGoogleUser) {
        // Google user completing their profile — just save to DB
        const currentUser = auth.currentUser;
        if (!currentUser) return rejectWithValue("No authenticated user found");
        uid = currentUser.uid;
        await updatePassword(currentUser, password);
      } else {
        // Check if email already exists before trying to create
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          return rejectWithValue(
            "An account with this email already exists. Please sign in instead."
          );
        }
        const userCred = await createUserWithEmailAndPassword(auth, email, password);
        uid = userCred.user.uid;
      }

      if (db) {
        await set(ref(db, `users/${uid}`), {
          name,
          email,
          phone,
          photoURL: auth.currentUser?.photoURL || null,
          createdAt: Date.now(),
        });
      }

      return { uid, email, name, phone };
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        return rejectWithValue(
          "An account with this email already exists. Please sign in instead."
        );
      }
      return rejectWithValue(err.message);
    }
  }
);

// Manual login
export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      if (!auth) return rejectWithValue("Firebase Auth not configured");
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCred.user.uid;

      let userData = { uid, email };
      if (db) {
        const snap = await get(ref(db, `users/${uid}`));
        if (snap.exists()) {
          const d = snap.val();
          userData = { uid, email, name: d.name, phone: d.phone, photoURL: userCred.user.photoURL || d.photoURL || null };
        }
      }
      return userData;
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        return rejectWithValue("Invalid email or password.");
      }
      return rejectWithValue(err.message);
    }
  }
);

// Google — used on BOTH signin and signup pages
// Logic:
//   - If user has a DB profile → direct login (no form needed)
//   - If no DB profile → return needsProfile: true so UI shows profile form
export const googleAuth = createAsyncThunk(
  "auth/googleAuth",
  async (_, { rejectWithValue }) => {
    try {
      if (!auth) return rejectWithValue("Firebase Auth not configured");

      const provider = new GoogleAuthProvider();
      const userCred = await signInWithPopup(auth, provider);
      const uid = userCred.user.uid;
      const email = userCred.user.email;
      const googleName = userCred.user.displayName || "";

      if (db) {
        const snap = await get(ref(db, `users/${uid}`));
        if (snap.exists()) {
          // Profile complete — direct login
          const d = snap.val();
          return { uid, email, name: d.name, phone: d.phone, photoURL: userCred.user.photoURL || d.photoURL || null, needsProfile: false };
        }
      }

      // New Google user — needs to fill name, phone, password
      return { uid, email, name: googleName, photoURL: userCred.user.photoURL || null, needsProfile: true };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: (() => {
    try {
      const s = localStorage.getItem("auth:v1");
      return s
        ? { user: JSON.parse(s), status: "idle", error: null }
        : { user: null, status: "idle", error: null };
    } catch (e) {
      return { user: null, status: "idle", error: null };
    }
  })(),
  reducers: {
    logout(state) {
      state.user = null;
      removeLocal();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.fulfilled, (state, action) => {
        state.user = action.payload;
        saveLocal(action.payload);
      })
      .addCase(signup.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload;
        saveLocal(action.payload);
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      })
      .addCase(googleAuth.fulfilled, (state, action) => {
        // Only set user in store if profile is complete
        if (!action.payload.needsProfile) {
          state.user = action.payload;
          saveLocal(action.payload);
        }
      })
      .addCase(googleAuth.rejected, (state, action) => {
        state.error = action.payload || action.error.message;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
